import {
    DEFAULT_PAGE_BACKGROUND, IFullPageData, IPageBodyData, IPageContentRenderer, IPageData, IPageHeadData,
    SectionAreaType
} from "@pageBuilder/typings";
import { PageStyleManager } from "./shared/style-managers/page-style-manager";
import { PopUpCascadeRenderer } from "./pop-up/cascade-renderers/pop-up-cascade-renderer";
import { PopUpStyleManager } from "./shared/style-managers/pop-up-style-manager";
import { SiteDesignStylesService } from "./services/site-design-styles.service";
import { PageBackgroundRenderer } from "./page/page-background-renderer";
import { CascadeRendererFactory } from "./shared/cascade-renderer-factory";
import { IHeaderSectionsAreaData, IMainSectionsAreaData, IFooterSectionsAreaData } from "./typings/section-area-data";
import { ElementUtil } from "./utils/element-util";
import { DocumentUtil } from "./utils/document-util";
import { ContentRendererBase } from "./content-renderer-base";
import { CookieWriterElementsProcessorFactory, IRenderingCommonContext, RenderingCommonContextUtil } from "@commonRendering/types";

export class PageContentRenderer extends ContentRendererBase implements IPageContentRenderer {

    public renderPage(doc: Document): void {
        const pageData = DocumentUtil.getPageStructure(doc);
        const renderingCommonContext = RenderingCommonContextUtil.getRenderingCommonContext(doc);

        if (!pageData) {
            console.error("No data-page-structure attribute found on html element.");
            return;
        }

        if (!renderingCommonContext) {
            console.error("Not rendering common context found on html document");
            return;
        }

        this.addWidgetAnimationScript(renderingCommonContext.renderedDoc);
        this.addSectionScrollEffectScript(renderingCommonContext.renderedDoc);
        if (renderingCommonContext.data.mode === "editor" || pageData.bodyData.headerArea) {
            this.addHeaderSectionAreaScript(renderingCommonContext.renderedDoc);
        }
        if (renderingCommonContext.data.mode === "published" || renderingCommonContext.data.mode === "prerendering") {
            this.addAnalyticsScript(renderingCommonContext.renderedDoc);
        }

        this.renderByCascade(doc, pageData, renderingCommonContext);

        doc.documentElement.removeAttribute("data-page-structure");
        if (renderingCommonContext.data.mode !== "editor") {
            if (pageData.popupData) {
                this.addPopupScript(renderingCommonContext.renderedDoc);
            }
            doc.documentElement.querySelectorAll("[data-element-json]").forEach(element => {
                element.removeAttribute("data-element-json");
            });
        }
    }

    private renderByCascade(doc: Document, data: IFullPageData, renderingCommonContext: IRenderingCommonContext): void {
        const pageStyleManager = PageStyleManager.create(doc);

        this.renderHeadElement(pageStyleManager, doc.head, data.headData);
        this.renderBodyElement(doc.body, data.bodyData, renderingCommonContext);

        if (renderingCommonContext.data.mode !== "editor") {
            if (data.popupData) {
                const popUpStyleManager = PopUpStyleManager.create(doc);
                const popUpCascadeRenderer = new PopUpCascadeRenderer(renderingCommonContext);
                const popup = popUpCascadeRenderer.createByCascade(data.popupData);
                popUpStyleManager.flush();
                doc.body.appendChild(popup);
            }

            this.processAccordingGdprSettings(doc);
        }

        pageStyleManager.flush();
    }

    private renderHeadElement(pageStyleManager: PageStyleManager, _headElement: HTMLHeadElement, data: IPageHeadData): void {
        const siteDesignStylesService = SiteDesignStylesService.create(pageStyleManager);
        siteDesignStylesService.applyStyles(data.commonStyles);
    }

    private renderBodyElement(bodyElement: HTMLElement, data: IPageBodyData, renderingCommonContext: IRenderingCommonContext): void {
        const renderingPlace = bodyElement.querySelector("[data-rendering-place]") as HTMLElement;

        if (!data.background) {
            data.background = DEFAULT_PAGE_BACKGROUND;
        }

        const backgroundRenderer = new PageBackgroundRenderer(renderingCommonContext);
        const backgroundElement = backgroundRenderer.createNew();
        backgroundRenderer.render(backgroundElement, data.background);
        renderingPlace.append(backgroundElement);

        if (data.headerArea) {
            const headerAreaRenderer = CascadeRendererFactory.create("HeaderSectionsArea", renderingCommonContext);
            const headerAreaElement = headerAreaRenderer.createByCascade(data.headerArea);
            renderingPlace.append(headerAreaElement);
        }

        const mainAreaRenderer = CascadeRendererFactory.create("MainSectionsArea", renderingCommonContext);
        const mainAreaElement = mainAreaRenderer.createByCascade(data.mainArea);
        renderingPlace.append(mainAreaElement);

        if (data.footerArea) {
            const footerAreaRenderer = CascadeRendererFactory.create("FooterSectionsArea", renderingCommonContext);
            const footerAreaElement = footerAreaRenderer.createByCascade(data.footerArea);
            renderingPlace.append(footerAreaElement);
        }

        renderingPlace.insertAdjacentHTML("afterend", renderingPlace.innerHTML);

        renderingPlace.remove();
    }

    private processAccordingGdprSettings(doc: Document): void {
        const processor = CookieWriterElementsProcessorFactory.createIfNecessary(doc);
        processor?.disableCookieWriters();
    }

    public getPageStructure(html: string): IPageData | null {
        const domParser = new DOMParser();
        const docTemplate = domParser.parseFromString(html, "text/html");

        return this.getDataByCascade(docTemplate.documentElement);
    }

    private getDataByCascade(doc: HTMLElement): IPageData {
        const renderingContext = RenderingCommonContextUtil.getRenderingCommonContext(doc.ownerDocument);
        const pageStyleManager = PageStyleManager.create(doc.ownerDocument);
        const backgroundRenderer = new PageBackgroundRenderer(renderingContext);
        const siteDesignStylesService = SiteDesignStylesService.create(pageStyleManager);

        const bodyElement = doc.querySelector("body") as HTMLElement;
        const backgroundElement = bodyElement.querySelector("[data-page-background]") as HTMLElement;
        const sectionAreas = bodyElement.querySelectorAll<HTMLElement>("[data-section-area]");

        let headerData: IHeaderSectionsAreaData | null = null;
        let mainData: IMainSectionsAreaData | null = null;
        let footerData: IFooterSectionsAreaData | null = null;

        const backgroundData = backgroundElement ? backgroundRenderer.getData(backgroundElement) : null;

        sectionAreas.forEach(sectionAreaElement => {
            const sectionAreaType = ElementUtil.resolveElementType(sectionAreaElement) as SectionAreaType;
            const cascadeRenderer = CascadeRendererFactory.create(sectionAreaType, renderingContext);

            switch (sectionAreaType) {
                case "HeaderSectionsArea":
                    headerData = cascadeRenderer.getDataByCascade(sectionAreaElement) as IHeaderSectionsAreaData;
                    break;
                case "MainSectionsArea":
                    mainData = cascadeRenderer.getDataByCascade(sectionAreaElement) as IMainSectionsAreaData;
                    break;
                case "FooterSectionsArea":
                    footerData = cascadeRenderer.getDataByCascade(sectionAreaElement) as IFooterSectionsAreaData;
                    break;
            }
        });

        const result: IPageData = {
            headData: {
                commonStyles: siteDesignStylesService.getStyles()
            },
            bodyData: {
                mainArea: mainData!,
            }
        };

        if (backgroundData) {
            result.bodyData.background = backgroundData;
        }

        if (headerData) {
            result.bodyData.headerArea = headerData;
        }

        if (footerData) {
            result.bodyData.footerArea = footerData;
        }

        return result;
    }

}

((w): void => {
    const pageRenderer = new PageContentRenderer();
    w["page-content-renderer"] = pageRenderer;
    pageRenderer.renderPage(w.document);
})(window);
